import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { motion, useInView } from 'framer-motion';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledProjectsSection = styled(motion.section)`
  max-width: 1600px;
  margin: 0 auto;
  padding: var(--space-5xl) var(--container-padding);
  position: relative;

  /* Background text */
  &::before {
    content: 'OTHER WORK';
    position: absolute;
    top: 5%;
    right: 5%;
    font-size: clamp(120px, 20vw, 280px);
    font-weight: 700;
    color: rgba(0, 0, 0, 0.02);
    letter-spacing: 0.05em;
    z-index: 0;
    pointer-events: none;
    font-family: var(--font-display);
    text-transform: uppercase;
  }

  .section-header {
    margin-bottom: var(--space-4xl);
    position: relative;
    z-index: 1;
  }

  .projects-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: var(--space-xl);
    position: relative;
    z-index: 1;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

const StyledProject = styled(motion.li)`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: var(--space-2xl);
    border-radius: var(--border-radius);
    background: var(--bg-elevated);
    border: 1px solid var(--border-color);
    transition: var(--transition-base);
    overflow: hidden;

    &:hover {
      border-color: var(--border-hover);
      background: var(--bg-tertiary);
      box-shadow: 0 12px 32px var(--shadow-md);
      transform: translateY(-6px);
    }
  }

  .project-top {
    ${({ theme }) => theme.mixins.flexBetween};
    margin-bottom: var(--space-lg);

    .folder {
      color: var(--text-primary);
      transition: var(--transition-base);
      opacity: 0.8;
      svg {
        width: 48px;
        height: 48px;
      }
    }

    .project-links {
      display: flex;
      gap: var(--space-sm);
      align-items: center;

      a {
        ${({ theme }) => theme.mixins.flexCenter};
        width: 36px;
        height: 36px;
        color: var(--text-secondary);
        transition: var(--transition-base);

        &:hover {
          opacity: 0.7;
          transform: translateY(-2px);
        }

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .project-title {
    margin: 0 0 var(--space-md) 0;
    font-size: clamp(24px, 3vw, 32px);
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -0.01em;
    color: var(--text-primary);
    font-family: var(--font-display);

    a {
      color: inherit;
      transition: var(--transition-base);
    }
  }

  .project-description {
    color: var(--text-tertiary);
    font-size: clamp(15px, 1.6vw, 17px);
    line-height: 1.7;
    opacity: 0.85;
    margin-bottom: var(--space-lg);
    flex-grow: 1;
    font-weight: 300;
  }

  .project-tech-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-sm);
    padding: 0;
    margin: var(--space-lg) 0 0 0;
    list-style: none;

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      color: var(--text-secondary);
      padding: var(--space-xs) var(--space-sm);
      background: var(--bg-secondary);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-sm);
      opacity: 0.8;
    }
  }
`;

const Projects = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              github
              external
            }
            html
          }
        }
      }
    }
  `);

  const [showMore, setShowMore] = useState(false);
  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();
  const isInView = useInView(revealTitle, { once: true, margin: '-100px' });

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const GRID_LIMIT = 6;
  const projects = data.projects.edges.filter(({ node }) => node);
  const firstSix = projects.slice(0, GRID_LIMIT);
  const projectsToShow = showMore ? projects : firstSix;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  const projectInner = node => {
    const { frontmatter, html } = node;
    const { github, external, title, tech } = frontmatter;

    return (
      <div className="project-inner">
        <header>
          <div className="project-top">
            <div className="folder">
              <Icon name="Folder" />
            </div>
            <div className="project-links">
              {github && (
                <motion.a
                  href={github}
                  aria-label="GitHub Link"
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}>
                  <Icon name="GitHub" />
                </motion.a>
              )}
              {external && (
                <motion.a
                  href={external}
                  aria-label="External Link"
                  className="external"
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}>
                  <Icon name="External" />
                </motion.a>
              )}
            </div>
          </div>

          <h3 className="project-title">
            <a href={external || github || '#'}>{title}</a>
          </h3>

          <div className="project-description" dangerouslySetInnerHTML={{ __html: html }} />
        </header>

        <footer>
          {tech && (
            <ul className="project-tech-list">
              {tech.map((techItem, i) => (
                <li key={i}>{techItem}</li>
              ))}
            </ul>
          )}
        </footer>
      </div>
    );
  };

  return (
    <StyledProjectsSection
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={containerVariants}>
      <motion.div className="section-header" ref={revealTitle} variants={itemVariants}>
        <h2 className="numbered-heading">Other Projects</h2>
      </motion.div>

      <ul className="projects-grid">
        {prefersReducedMotion ? (
          <>
            {projectsToShow &&
              projectsToShow.map(({ node }, i) => (
                <StyledProject key={i}>{projectInner(node)}</StyledProject>
              ))}
          </>
        ) : (
          <>
            {projectsToShow &&
              projectsToShow.map(({ node }, i) => (
                <StyledProject
                  key={i}
                  ref={el => (revealProjects.current[i] = el)}
                  variants={itemVariants}
                  whileHover={{ y: -8, transition: { type: 'spring', stiffness: 300 } }}
                  style={{
                    transitionDelay: `${i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0}ms`,
                  }}>
                  {projectInner(node)}
                </StyledProject>
              ))}
          </>
        )}
      </ul>

      {projects.length > GRID_LIMIT && (
        <motion.div
          style={{ textAlign: 'center', marginTop: 'var(--space-4xl)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}>
          <motion.button
            className="more-button"
            onClick={() => setShowMore(!showMore)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              ...({ theme }) => theme.mixins.bigButton,
              background: 'transparent',
              borderColor: 'var(--border-color)',
              color: 'var(--text-secondary)',
            }}>
            Show {showMore ? 'Less' : 'More'}
          </motion.button>
        </motion.div>
      )}
    </StyledProjectsSection>
  );
};

export default Projects;
